import type { MutableRefObject } from 'react'

type TopUpPostResult<T> =
  | { response: Response; data: { data: T }; error?: undefined }
  | { response: Response; data?: undefined; error: unknown }

/**
 * Idempotency-key lifecycle and error shaping shared by the top-up purchase
 * hooks (credits and meeting hours) — the double-charge protection, kept in
 * one place so the two purchases cannot drift. The key must SURVIVE
 * network-level failures (timeout, connection drop — the server may have
 * charged before the response was lost) so the retry replays instead of
 * double-charging, and must ROTATE once the server returns any definitive
 * outcome: Stripe replays error results for reused keys, so keeping the key
 * after a decline would trap the user even after they fix their card. A 5xx
 * is ambiguous, so the key survives it.
 */
export async function postTopUpPurchase<T>(
  pendingKeys: MutableRefObject<Map<string, string>>,
  options: {
    /** What is being bought (the amount); an unresolved key belongs to this request alone. */
    request: string
    /** Performs the typed POST for this purchase, carrying the key in its body. */
    post: (idempotencyKey: string) => Promise<TopUpPostResult<T>>
    fallbackMessage: string
    /** Replaces any server message that mentions the payment method. */
    paymentMethodMessage: string
  },
): Promise<T> {
  // Keyed by request: another amount is another purchase with its own key, and the unresolved
  // one keeps its key for its own retry (the server refuses a key reused with another amount).
  const idempotencyKey = pendingKeys.current.get(options.request) ?? crypto.randomUUID()
  pendingKeys.current.set(options.request, idempotencyKey)
  // Throws on network failure, leaving the key in place for the retry.
  const response = await options.post(idempotencyKey)
  if (response.response.status < 500) {
    pendingKeys.current.delete(options.request)
  }

  if (response.error !== undefined || response.data === undefined) {
    const errorData = response.error as { error?: string; success?: boolean }
    const errorMessage =
      typeof errorData === 'string'
        ? errorData
        : typeof errorData?.error === 'string'
          ? errorData.error
          : options.fallbackMessage
    throw new Error(errorMessage.includes('payment method') ? options.paymentMethodMessage : errorMessage)
  }

  return response.data.data
}
